import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import {
  AlertTriangle,
  CheckCircle2,
  FileText,
  Globe,
  Info,
  Loader2,
  Lock,
  Save,
  Shield,
  ShieldCheck,
  Timer,
  Eye,
  EyeOff,
  Scan,
  MapPin,
  TestTube, MailX
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { DeliverabilityManager } from '../../components/settings/DeliverabilityManager';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Slider } from '../../components/ui/slider';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import { toast } from 'sonner';

// Region display info
const REGION_INFO = {
  GDPR: {
    name: 'GDPR',
    fullName: 'General Data Protection Regulation',
    regions: 'EU / UK',
    icon: '🇪🇺',
  },
  DPDP: {
    name: 'DPDP',
    fullName: 'Digital Personal Data Protection Act',
    regions: 'India',
    icon: '🇮🇳',
  },
};

export function ComplianceSettings() {
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  
  // Settings state
  const [settings, setSettings] = useState(null);
  const [disclaimers, setDisclaimers] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  
  // Form state
  const [explicitConsentEnabled, setExplicitConsentEnabled] = useState(false);
  const [retentionDays, setRetentionDays] = useState(30);
  const [hasChanges, setHasChanges] = useState(false);

  const getToken = () => localStorage.getItem('access_token');

  const fetchSettings = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const [settingsRes, disclaimersRes, auditRes] = await Promise.all([
        api.get('/api/compliance/settings').catch(() => ({ data: null })),
        api.get('/api/compliance/disclaimers').catch(() => ({ data: null })),
        api.get('/api/compliance/audit?days=30&limit=50').catch(() => ({ data: null })),
      ]);

      if (settingsRes.data) {
        setSettings(settingsRes.data);
        setExplicitConsentEnabled(settingsRes.data.explicit_consent_enabled || false);
        setRetentionDays(settingsRes.data.retention_days || 30);
      }
      
      if (disclaimersRes.data) {
        setDisclaimers(disclaimersRes.data);
      }
      
      if (auditRes.data) {
        setAuditLogs(auditRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch compliance settings:', error);
      toast.error('Failed to load compliance settings');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchSettings();
    }
  }, [fetchSettings, isAuthenticated]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const response = await api.put('/api/compliance/settings', {
        explicit_consent_enabled: explicitConsentEnabled,
        retention_days: retentionDays,
      });
      setSettings(response.data);
      setHasChanges(false);
      toast.success('Compliance settings saved successfully');
      fetchSettings();
    } catch (error) {
      console.error('Failed to save settings:', error);
      const detail = error.response?.data?.detail;
      toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleExplicitConsentChange = (checked) => {
    setExplicitConsentEnabled(checked);
    setHasChanges(true);
  };

  const handleRetentionChange = (value) => {
    setRetentionDays(value[0]);
    setHasChanges(true);
  };

  if (loading || !isAuthenticated) {
    return (
      <div className="space-y-6" data-testid="compliance-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="compliance-settings">
      {/* Page Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Shield className="h-6 w-6" />
            Compliance Settings
          </h1>
          <p className="text-muted-foreground">
            Region-specific data protection compliance (GDPR / DPDP India)
          </p>
        </div>
        {hasChanges && (
          <Button onClick={handleSaveSettings} disabled={saving}>
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save Changes
          </Button>
        )}
      </div>

      {/* Info Banner */}
      <Alert>
        <ShieldCheck className="h-4 w-4" />
        <AlertTitle>Compliance Gates Execution</AlertTitle>
        <AlertDescription>
          Voice AI calls require consent before proceeding. Region is detected from tenant billing country or caller phone number.
        </AlertDescription>
      </Alert>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="settings" className="gap-2">
            <Shield className="h-4 w-4" />
            Settings
          </TabsTrigger>
          <TabsTrigger value="pii" className="gap-2">
            <Eye className="h-4 w-4" />
            PII Protection
          </TabsTrigger>
          <TabsTrigger value="disclaimers" className="gap-2">
            <FileText className="h-4 w-4" />
            Disclaimers
          </TabsTrigger>
          <TabsTrigger value="audit" className="gap-2">
            <Lock className="h-4 w-4" />
            Audit Log
          </TabsTrigger>
          <TabsTrigger value="deliverability" className="gap-2">
            <MailX className="h-4 w-4" />
            Deliverability
          </TabsTrigger>
        </TabsList>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Consent Mode */}
            <Card data-testid="consent-mode-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5" />
                  Consent Mode
                </CardTitle>
                <CardDescription>
                  Choose how consent is captured during voice calls
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="explicit-consent">Explicit Consent Required</Label>
                    <p className="text-sm text-muted-foreground">
                      When enabled, callers must say &ldquo;Yes&rdquo; to proceed
                    </p>
                  </div>
                  <Switch
                    id="explicit-consent"
                    checked={explicitConsentEnabled}
                    onCheckedChange={handleExplicitConsentChange}
                    data-testid="explicit-consent-switch"
                  />
                </div>
                
                <div className="rounded-lg border p-3 bg-muted/50">
                  <p className="text-sm font-medium mb-1">
                    Current Mode: {explicitConsentEnabled ? 'Explicit' : 'Implied'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {explicitConsentEnabled
                      ? 'Callers will be asked to confirm consent with Yes/No prompt'
                      : 'Consent is implied when caller continues after disclaimer'}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Data Retention */}
            <Card data-testid="retention-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Timer className="h-5 w-5" />
                  Data Retention
                </CardTitle>
                <CardDescription>
                  How long voice call data is retained before deletion
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <Label>Retention Period</Label>
                    <span className="text-sm font-medium">{retentionDays} days</span>
                  </div>
                  <Slider
                    value={[retentionDays]}
                    onValueChange={handleRetentionChange}
                    min={7}
                    max={365}
                    step={1}
                    data-testid="retention-slider"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>7 days (min)</span>
                    <span>365 days (max)</span>
                  </div>
                </div>
                
                <div className="rounded-lg border p-3 bg-muted/50">
                  <p className="text-xs text-muted-foreground">
                    <Info className="inline h-3 w-3 mr-1" />
                    Changes apply prospectively to new calls only. Existing data follows previous settings.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Supported Regions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Supported Regions
              </CardTitle>
              <CardDescription>
                Region is automatically detected from tenant billing country or caller phone
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                {Object.entries(REGION_INFO).map(([key, info]) => (
                  <div
                    key={key}
                    className="flex items-start gap-3 rounded-lg border p-4"
                  >
                    <span className="text-2xl">{info.icon}</span>
                    <div>
                      <p className="font-medium">{info.name}</p>
                      <p className="text-sm text-muted-foreground">{info.fullName}</p>
                      <Badge variant="outline" className="mt-2">{info.regions}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PII Protection Tab */}
        <TabsContent value="pii" className="space-y-6">
          <PIIProtectionTab />
        </TabsContent>

        {/* Disclaimers Tab */}
        <TabsContent value="disclaimers" className="space-y-6">
          <Alert variant="warning" className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950">
            <Lock className="h-4 w-4 text-amber-600" />
            <AlertTitle className="text-amber-800 dark:text-amber-200">Read-Only</AlertTitle>
            <AlertDescription className="text-amber-700 dark:text-amber-300">
              Disclaimer text is legally required and cannot be modified.
            </AlertDescription>
          </Alert>

          {disclaimers && (
            <div className="space-y-4">
              <Accordion type="single" collapsible className="w-full">
                {Object.entries(disclaimers.disclaimers || {}).map(([region, disclaimer]) => (
                  <AccordionItem key={region} value={region}>
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{REGION_INFO[region]?.icon}</span>
                        <div className="text-left">
                          <p className="font-medium">{REGION_INFO[region]?.name} Disclaimer</p>
                          <p className="text-sm text-muted-foreground font-normal">
                            {REGION_INFO[region]?.regions}
                          </p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="rounded-lg border p-4 bg-muted/50 space-y-3">
                        <div>
                          <Label className="text-xs">Version</Label>
                          <p className="text-sm">{disclaimer.version}</p>
                        </div>
                        <div>
                          <Label className="text-xs">Disclaimer Text</Label>
                          <p className="text-sm italic">&ldquo;{disclaimer.text}&rdquo;</p>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
                
                {/* Explicit Consent Prompt */}
                <AccordionItem value="explicit-prompt">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-blue-500" />
                      <div className="text-left">
                        <p className="font-medium">Explicit Consent Prompt</p>
                        <p className="text-sm text-muted-foreground font-normal">
                          Used when explicit consent is enabled
                        </p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="rounded-lg border p-4 bg-muted/50 space-y-3">
                      <div>
                        <Label className="text-xs">Version</Label>
                        <p className="text-sm">{disclaimers.explicit_consent_prompt?.version}</p>
                      </div>
                      <div>
                        <Label className="text-xs">Prompt Text</Label>
                        <p className="text-sm italic">&ldquo;{disclaimers.explicit_consent_prompt?.text}&rdquo;</p>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          )}
        </TabsContent>

        {/* Audit Log Tab */}
        <TabsContent value="audit" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Compliance Audit Log
              </CardTitle>
              <CardDescription>
                Immutable record of all compliance-related events (last 30 days)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {auditLogs.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {auditLogs.map((log) => (
                    <div
                      key={log.id}
                      className="flex items-start gap-3 rounded-lg border p-3"
                    >
                      <div className="flex-shrink-0">
                        {log.event_type === 'consent_recorded' && (
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                        )}
                        {log.event_type === 'settings_updated' && (
                          <Shield className="h-4 w-4 text-blue-500" />
                        )}
                        {log.event_type === 'compliance_check' && (
                          <ShieldCheck className="h-4 w-4 text-purple-500" />
                        )}
                        {log.event_type === 'transcript_anonymized' && (
                          <AlertTriangle className="h-4 w-4 text-amber-500" />
                        )}
                        {log.event_type === 'recording_deleted' && (
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium truncate">
                            {log.event_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </p>
                          <Badge variant="outline" className="text-xs flex-shrink-0">
                            {log.immutable ? 'Immutable' : 'Mutable'}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {new Date(log.timestamp).toLocaleString()}
                        </p>
                        {log.details && (
                          <div className="mt-2 text-xs bg-muted/50 rounded p-2">
                            {Object.entries(log.details).map(([key, value]) => (
                              <div key={key}>
                                <span className="text-muted-foreground">{key}: </span>
                                <span>{String(value)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Lock className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No audit events recorded yet</p>
                  <p className="text-sm">Events will appear here as compliance actions occur</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Deliverability Tab */}
        <TabsContent value="deliverability" className="space-y-6">
          <DeliverabilityManager />
        </TabsContent>

      </Tabs>
    </div>
  );
}

export default ComplianceSettings;


// ============================================
// PII PROTECTION TAB COMPONENT
// ============================================

function PIIProtectionTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [piiSettings, setPiiSettings] = useState(null);
  const [dataResidency, setDataResidency] = useState(null);
  const [regions, setRegions] = useState([]);
  const [testText, setTestText] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchPIISettings();
  }, []);

  const fetchPIISettings = async () => {
    setLoading(true);
    try {
      const [settingsRes, residencyRes, regionsRes] = await Promise.all([
        api.get('/api/compliance/pii/settings'),
        api.get('/api/compliance/pii/data-residency'),
        api.get('/api/compliance/pii/regions')
      ]);
      
      setPiiSettings(settingsRes.data);
      setDataResidency(residencyRes.data);
      setRegions(regionsRes.data.regions || []);
    } catch (error) {
      console.error('Failed to load PII settings:', error);
      toast.error('Failed to load PII protection settings');
    } finally {
      setLoading(false);
    }
  };

  const savePIISettings = async () => {
    setSaving(true);
    try {
      await api.put('/api/compliance/pii/settings', {
        enabled: piiSettings.enabled,
        categories: piiSettings.categories,
        masking_strategy: piiSettings.masking_strategy,
        block_on_detection: piiSettings.block_on_detection,
        log_detections: piiSettings.log_detections,
        region: piiSettings.region
      });
      toast.success('PII protection settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const testPIIScan = async () => {
    if (!testText.trim()) {
      toast.error('Please enter text to scan');
      return;
    }
    
    setTesting(true);
    try {
      const res = await api.post('/api/compliance/pii/scan', {
        text: testText,
        masking_strategy: piiSettings?.masking_strategy || 'redact'
      });
      setTestResult(res.data);
    } catch (error) {
      toast.error('Failed to scan text');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* PII Detection Settings */}
      <Card data-testid="pii-settings-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            PII Detection & Masking
          </CardTitle>
          <CardDescription>
            Automatically detect and mask personally identifiable information in AI prompts and responses
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable Toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Enable PII Detection</Label>
              <p className="text-sm text-muted-foreground">
                Scan all AI inputs/outputs for sensitive data
              </p>
            </div>
            <Switch
              checked={piiSettings?.enabled}
              onCheckedChange={(checked) => setPiiSettings(prev => ({ ...prev, enabled: checked }))}
              data-testid="pii-enabled-switch"
            />
          </div>

          {/* Masking Strategy */}
          <div className="space-y-3">
            <Label>Masking Strategy</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { value: 'redact', label: 'Redact', desc: '[REDACTED]' },
                { value: 'partial', label: 'Partial', desc: '***-**-1234' },
                { value: 'hash', label: 'Hash', desc: '[HASH:a1b2c3]' },
                { value: 'tokenize', label: 'Tokenize', desc: '[TOKEN:xyz]' }
              ].map((strategy) => (
                <button
                  key={strategy.value}
                  onClick={() => setPiiSettings(prev => ({ ...prev, masking_strategy: strategy.value }))}
                  className={`p-3 rounded-lg border text-left transition-colors ${
                    piiSettings?.masking_strategy === strategy.value
                      ? 'border-primary bg-primary/5'
                      : 'hover:border-primary/50'
                  }`}
                  data-testid={`strategy-${strategy.value}`}
                >
                  <p className="font-medium text-sm">{strategy.label}</p>
                  <p className="text-xs text-muted-foreground mt-1">{strategy.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Block on Detection */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Block on PII Detection</Label>
              <p className="text-sm text-muted-foreground">
                Prevent AI execution if PII is detected (strict mode)
              </p>
            </div>
            <Switch
              checked={piiSettings?.block_on_detection}
              onCheckedChange={(checked) => setPiiSettings(prev => ({ ...prev, block_on_detection: checked }))}
              data-testid="block-pii-switch"
            />
          </div>

          {/* Compliance Region */}
          <div className="space-y-3">
            <Label>Compliance Region</Label>
            <div className="flex flex-wrap gap-2">
              {regions.map((r) => (
                <Badge
                  key={r.region}
                  variant={piiSettings?.region === r.region ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => setPiiSettings(prev => ({ ...prev, region: r.region }))}
                >
                  {r.name}
                </Badge>
              ))}
            </div>
          </div>

          <Button onClick={savePIISettings} disabled={saving} data-testid="save-pii-settings-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save PII Settings
          </Button>
        </CardContent>
      </Card>

      {/* PII Test Scanner */}
      <Card data-testid="pii-scanner-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Scan className="h-5 w-5" />
            Test PII Scanner
          </CardTitle>
          <CardDescription>
            Test the PII detection on sample text
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Sample Text</Label>
            <textarea
              value={testText}
              onChange={(e) => setTestText(e.target.value)}
              placeholder="Enter text with PII to test (e.g., 'Contact me at john@example.com or 555-123-4567')"
              className="w-full h-24 p-3 rounded-lg border bg-background resize-none"
              data-testid="pii-test-input"
            />
          </div>
          
          <Button onClick={testPIIScan} disabled={testing} variant="outline" data-testid="test-pii-btn">
            {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <TestTube className="h-4 w-4 mr-2" />}
            Scan for PII
          </Button>

          {testResult && (
            <div className="space-y-3 mt-4">
              <div className="flex items-center gap-2">
                {testResult.has_pii ? (
                  <Badge className="bg-amber-100 text-amber-800">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    PII Detected ({testResult.match_count})
                  </Badge>
                ) : (
                  <Badge className="bg-green-100 text-green-800">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    No PII Found
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground">
                  {testResult.detection_time_ms.toFixed(2)}ms
                </span>
              </div>
              
              {testResult.has_pii && (
                <>
                  <div>
                    <Label className="text-xs">Categories Found</Label>
                    <div className="flex gap-1 mt-1">
                      {testResult.categories_found.map(cat => (
                        <Badge key={cat} variant="outline" className="text-xs">{cat}</Badge>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-xs">Masked Output</Label>
                    <div className="p-3 rounded-lg bg-muted/50 text-sm font-mono mt-1">
                      {testResult.masked_text}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Data Residency */}
      <Card data-testid="data-residency-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Data Residency
          </CardTitle>
          <CardDescription>
            Configure where your data is stored and processed
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Primary Region</Label>
              <p className="font-medium">{dataResidency?.primary_region || 'us-east'}</p>
            </div>
            <div>
              <Label className="text-xs">Compliance Status</Label>
              <Badge variant={dataResidency?.compliance_status === 'compliant' ? 'default' : 'warning'}>
                {dataResidency?.compliance_status || 'compliant'}
              </Badge>
            </div>
            <div>
              <Label className="text-xs">Data Localization</Label>
              <p className="text-sm">{dataResidency?.data_localization ? 'Enabled' : 'Disabled'}</p>
            </div>
            <div>
              <Label className="text-xs">Cross-Border Transfer</Label>
              <p className="text-sm">{dataResidency?.cross_border_transfer ? 'Allowed' : 'Blocked'}</p>
            </div>
          </div>
          
          {dataResidency?.allowed_regions?.length > 0 && (
            <div>
              <Label className="text-xs">Allowed Regions</Label>
              <div className="flex gap-1 mt-1">
                {dataResidency.allowed_regions.map(region => (
                  <Badge key={region} variant="outline" className="text-xs">{region}</Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
